<template>
  <div class="overflow-y-scroll flex-grow pa-2 pt-4">
    <div class="notes-module-body">
      <v-expansion-panels variant="accordion" flat>
        <v-expansion-panel
            v-for="(appointment, i) in appointmentHistory"
            :key="`patient-list-item-${appointment.uuid}`"
            :class="['panel-bg', {'panel-border': i !== appointmentHistory.length - 1}]"
        >
          <v-expansion-panel-title>
            <div class="flex items-center">
              <PatientListItem
                  :image-url="patient?.avatar"
                  :title="patient?.name"
                  :timestamp="$dayjs(appointment.starts_at).utc().format('YYYY-MM-DD HH:mm:ss')"
                  no-padding
                  style="flex-grow: 0; width: 260px; max-width: 260px; margin-right: 8px;"
              />
              <div
                  class="flex-col"
                  style="flex-grow: 0; width: 138px; max-width: 138px; margin-right: 8px;"
              >
                <span class="text-caption font-semibold gray--text mb-1">
                  Time in appointment
                </span>
                <span
                    class="block text-body-1"
                    v-text="formatTime(appointment)"
                ></span>
              </div>
            </div>

          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="flex items-start">
              <div class="flex flex-col text-caption"
                   v-if="appointment.doctors.length > 0"
                   style="flex-grow: 0; max-width: 400px; margin-right: 8px;">
                <div
                    class="flex flex-column"
                    style="margin: 0 0 10px"
                >
                  <span class="gray--text font-weight-bold">
                    Scheduled
                  </span>
                </div>
                <div class="flex flex-column" >
                  <template v-for="(doctor, i) in appointment.doctors" :key="`appointment-${i}-${doctor.doctor?.uuid}`">
                    <div v-if="doctor.doctor">
                      <TextCard
                          class="list-group-item mb-2 cursor-pointer"
                          unripple
                          :text="`${$dayjs.utc(doctor.scheduled_at).tz().format('MMM D, YYYY hh:mm A')} | ${doctor.doctor.name}`"
                          :initials="$filters.initials(doctor.doctor.name)"
                          :initialsColor="doctor.doctor.color"
                          :no-border="true"
                      />
                    </div>
                  </template>
                </div>
              </div >
              <div :class="['flex','flex-col','text-caption', {'shift-to-center': appointment.doctors.length < 1}]" style="flex-grow: 0; margin-left: 5px; max-width: 400px; margin-right: 8px;">
                <div class="flex items-center mb-1"
                     v-if="!!appointment.checkedin_at || !!appointment.arrived_at || !!appointment.ended_at"
                >
                  <span class="gray--text font-weight-bold">
                    Stats
                  </span>
                </div>
                <div
                    v-if="!!appointment.checkedin_at"
                    class="flex items-center mb-1"
                >
                  <span class="gray--text text--lighten-3">
                    Arrived at
                  </span>
                  <span class="gray--text ml-1">
                    {{ $dayjs.utc(appointment.checkedin_at).tz().format('hh:mma') }}
                  </span>
                </div>
                  <div
                          v-if="!!appointment.arrived_at"
                          class="flex items-center mb-1"
                  >
                  <span class="gray--text text--lighten-3">
                    Appointment started
                  </span>
                      <span class="gray--text ml-1">
                    {{ $dayjs.utc(appointment.arrived_at).tz().format('hh:mma') }}
                  </span>
                  </div>
                <div
                    v-if="!!appointment.ended_at"
                    class="flex items-center"
                >
                <span class="gray--text text--lighten-3">
                  Appointment ended
                </span>
                  <span class="gray--text ml-1">
                  {{ $dayjs.utc(appointment.ended_at).tz().format('hh:mma') }}
                </span>
                </div>

                <div
                    class="flex flex-column"
                    style="margin: 30px 0 10px"
                    v-if="appointment.treatments.length > 0"
                >
                  <span class="gray--text font-weight-bold">
                    Treatments
                  </span>
                </div>

                <div
                    class="flex flex-column"
                    v-if="appointment.treatments.length > 0"
                >
                  <template v-for="(treatment, i) in appointment.treatments" :key="`appointment-${i}-${treatment.treatment?.uuid || treatment.uuid}`">
                    <div v-if="treatment.treatment">
                      <TextCard
                          class="list-group-item mb-2 cursor-pointer"
                          unripple
                          :text="treatment.treatment.full_name"
                          :initials="$filters.initials(treatment.doctor?.name)"
                          :initialsColor="treatment.doctor?.color"
                          :duration="treatment.time_elapsed"
                          :no-border="true"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {mapState} from 'vuex';
import PatientListItem from '@/components/basic/PatientListItem.vue';
import TextCard from '@/components/cards/TextCard.vue';

export default defineComponent({
  name: 'AppointmentsList',
  components: {PatientListItem, TextCard },
  data() {
    return {};
  },
  computed: {
  ...mapState([
      'appointmentHistory',
    ]),
  ...mapState({
    // @ts-ignore
    patient: (state) => state.appointment.patient,
  }),
  },
  methods: {
    formatTime(appointment) {
      const t = this.$dayjs(appointment.ended_at).unix() - this.$dayjs(appointment.starts_at).unix();

      if (!appointment.starts_at) {
        return 'Not started!';
      }

      if (!appointment.ended_at) {
        return 'Not completed!';
      }

      const isLongerThanOneHour = t >= 3600;
      return `${
          this.$dayjs
              .utc(this.$dayjs.duration(t * 1000).asMilliseconds())
              .format(isLongerThanOneHour ? 'hh:mm' : 'mm:ss')
      }${
          isLongerThanOneHour ? 'm' : 's'
      }`;
    },
  },
});
</script>

<style scoped>
.panel-bg {
  background-color: #fafafa !important;
}
.shift-to-center {
  margin-left: 270px !important;
}
</style>
